import {GameTypeEnum, VictoryPointDataController} from '@colonist/game-base-types'
import {
    type GameState,
    type PlayerColor,
    type PlayerState,
    type GameLogState,
    type GameEventHistory,
    type VictoryPointState,
    type NestedPartial,
    type MechanicSpecialBuildPhaseState,
    type FogTileGroupStates,
    type GameChatState,
    type MechanicDevelopmentCardsState,
    type MechanicProgressCardsState,
} from '@colonist/game-states'
import {CardDataController} from '@colonist/game-card-types'
import {ArrayUtils, ObjectUtils} from '@colonist/utils'
import {applyDiffs} from './GameStateDiffUtils.ts'
import {AccessLevel, type BigId, type ColonistMembershipType} from '@colonist/types'

export function filterEventHistoryForPerspectives(eventHistory: GameEventHistory, perspectives: PlayerColor[]): GameEventHistory {
    eventHistory.eventsPerspectives = perspectives

    const filteredEvents = eventHistory.events.map((event) => ({
        input: event.input,
        stateChange: filterGameStateForPerspectives(event.stateChange, perspectives),
    }))

    const filteredNonEmptyEvents = filteredEvents.filter((event) => !ObjectUtils.isNestedObjectEmpty(event.stateChange))

    return {
        ...eventHistory,
        initialState: filterGameStateForPerspectives(eventHistory.initialState, perspectives),
        events: filteredNonEmptyEvents,
    }
}

export function filterGameStateForPerspectives<InputType extends NestedPartial<GameState>>(partialGameState: InputType, perspectives: PlayerColor[]): InputType {
    const filteredGameState = structuredClone(partialGameState)

    for(const key in filteredGameState.playerStates) {
        const playerColor = Number(key) as PlayerColor
        const playerState = filteredGameState.playerStates[key] as NestedPartial<PlayerState> | undefined
        if(playerState == undefined) continue

        filterPlayerStateForPerspectives(playerState, playerColor, perspectives)
    }

    if(filteredGameState.gameLogState !== undefined) filterGameLogsForPerspectives(filteredGameState.gameLogState, perspectives)
    if(filteredGameState.gameChatState !== undefined) filterGameChatForPerspectives(filteredGameState.gameChatState, perspectives)
    if(filteredGameState.mapState?.fogTileGroupStates != undefined) filterFogTileState(filteredGameState.mapState.fogTileGroupStates)
    if(filteredGameState.mechanicSpecialBuildPhaseState != undefined) filterSpecialBuildPhaseStateForPlayers(filteredGameState.mechanicSpecialBuildPhaseState, perspectives)
    if(filteredGameState.mechanicDevelopmentCardsState != undefined) filterDevelopmentCardStateForRecipient(filteredGameState.mechanicDevelopmentCardsState, perspectives)
    if(filteredGameState.mechanicProgressCardsState != undefined) filterProgressCardStateForRecipient(filteredGameState.mechanicProgressCardsState, perspectives)

    return filteredGameState
}

export function filterPlayerStateForPerspectives(playerState: NestedPartial<PlayerState>, playerStateColor: PlayerColor, perspectives: PlayerColor[]): NestedPartial<PlayerState> {
    if(perspectives.includes(playerStateColor)) return playerState

    if(playerState.victoryPointsState !== undefined) filterVictoryPointStateMakePrivate(playerState.victoryPointsState)
    if(playerState.resourceCards?.cards !== undefined) playerState.resourceCards.cards = CardDataController.getCardBacksForCards(playerState.resourceCards.cards)

    return playerState
}

function filterVictoryPointStateMakePrivate(victoryPointState: VictoryPointState) {
    for(const victoryPointType of Object.keys(victoryPointState)) {
        const victoryPointData = VictoryPointDataController.getVictoryPointByType(Number(victoryPointType))
        if(victoryPointData == undefined) continue
        if(victoryPointData.private) delete victoryPointState[victoryPointType]
    }
}

export function getCurrentGameState(gameEventHistory: GameEventHistory): GameState {
    const currentState = structuredClone(gameEventHistory.initialState)

    for(const gameEvent of gameEventHistory.events) {
        applyDiffs(currentState, [gameEvent.stateChange])
    }

    return currentState
}

export function filterGameLogsForPerspectives(gameLogState: NestedPartial<GameLogState>, perspectives: PlayerColor[]) {
    const isSpectator = perspectives.length === 0

    isSpectator ? filterGameLogsForSpectators(gameLogState) : filterGameLogsForPlayers(gameLogState, perspectives)
}

function filterGameLogsForSpectators(gameLogState: NestedPartial<GameLogState>) {
    for(const gameLogIndex in gameLogState) {
        const gameLogData = gameLogState[gameLogIndex]

        if(gameLogData == undefined) continue

        const sendToSpectators = gameLogData.toSpectators != false
        if(sendToSpectators) continue

        delete gameLogState[gameLogIndex]
    }
}

function filterGameLogsForPlayers(gameLogState: NestedPartial<GameLogState>, perspectives: PlayerColor[]) {
    for(const gameLogIndex in gameLogState) {
        const gameLogData = gameLogState[gameLogIndex]

        if(gameLogData == undefined) continue
        if(gameLogData.specificRecipients == undefined) continue
        if(ArrayUtils.isSomeElementIncluded(gameLogData.specificRecipients, perspectives)) continue

        delete gameLogState[gameLogIndex]
    }
}

export function filterGameChatForPerspectives(gameChatState: NestedPartial<GameChatState>, perspectives: PlayerColor[]) {
    const isSpectator = perspectives.length === 0

    isSpectator ? filterGameChatsForSpectators(gameChatState) : filterGameChatsForPlayers(gameChatState, perspectives)
}

function filterGameChatsForSpectators(gameChatState: NestedPartial<GameChatState>) {
    for(const gameChatIndex in gameChatState) {
        const gameChatData = gameChatState[gameChatIndex]

        if(gameChatData == undefined) continue

        const sendToSpectator = gameChatData.toSpectators != false
        if(sendToSpectator) continue

        delete gameChatState[gameChatIndex]
    }
}

function filterGameChatsForPlayers(gameChatState: NestedPartial<GameChatState>, perspectives: PlayerColor[]) {
    for(const gameChatIndex in gameChatState) {
        const gameChatData = gameChatState[gameChatIndex]

        if(gameChatData == undefined) continue
        if(gameChatData.recipients == undefined) continue
        if(ArrayUtils.isSomeElementIncluded(gameChatData.recipients, perspectives)) continue

        delete gameChatState[gameChatIndex]
    }
}

function filterFogTileState(fogTileStates: NestedPartial<FogTileGroupStates>) {
    for(const fogTileGroupKey in fogTileStates) {
        const fogTileState = fogTileStates[fogTileGroupKey]
        if(fogTileState == undefined) continue

        delete fogTileState.possibleDiceNumbers
        delete fogTileState.possibleTileTypes
    }
}

function filterSpecialBuildPhaseStateForPlayers(specialBuildPhaseState: NestedPartial<MechanicSpecialBuildPhaseState>, perspectives: PlayerColor[]) {
    if(specialBuildPhaseState.playerStates == undefined) return
    for(const playerStateKey in specialBuildPhaseState.playerStates) {
        const playerColor = Number(playerStateKey) as PlayerColor
        const playerState = specialBuildPhaseState.playerStates[playerColor]
        if(playerState == undefined) continue
        if(perspectives.includes(playerColor)) continue

        delete playerState.isRequesting
    }
}

export function filterDevelopmentCardStateForRecipient(developmentCardState: NestedPartial<MechanicDevelopmentCardsState>, perspectives: PlayerColor[]) {
    if(developmentCardState.bankDevelopmentCards?.cards != undefined) developmentCardState.bankDevelopmentCards.cards = CardDataController.getCardBacksForCards(developmentCardState.bankDevelopmentCards.cards)

    for(const playerColorKey in developmentCardState.players) {
        const playerColor = Number(playerColorKey) as PlayerColor
        if(perspectives.includes(playerColor)) continue

        const playerDevelopmentCardState = developmentCardState.players[playerColor]
        if(playerDevelopmentCardState == undefined) continue

        if(playerDevelopmentCardState.developmentCards?.cards != undefined) playerDevelopmentCardState.developmentCards.cards = CardDataController.getCardBacksForCards(playerDevelopmentCardState.developmentCards.cards)
        if(playerDevelopmentCardState.developmentCardsBoughtThisTurn != undefined) delete playerDevelopmentCardState.developmentCardsBoughtThisTurn
    }
}

export function filterProgressCardStateForRecipient(progressCardState: NestedPartial<MechanicProgressCardsState>, perspectives: PlayerColor[]) {
    if(progressCardState.bankProgressCards?.cards != undefined) progressCardState.bankProgressCards.cards = CardDataController.getCardBacksForCards(progressCardState.bankProgressCards.cards)

    for(const playerColorKey in progressCardState.players) {
        const playerColor = Number(playerColorKey) as PlayerColor
        if(perspectives.includes(playerColor)) continue

        const playerProgressCardState = progressCardState.players[playerColor]
        if(playerProgressCardState == undefined) continue

        if(playerProgressCardState.progressCards?.cards != undefined) playerProgressCardState.progressCards.cards = CardDataController.getCardBacksForCards(playerProgressCardState.progressCards.cards)
    }
}

export function getAllowedPerspectivesForUser(allPlayerColors: PlayerColor[], playersColorsControlledByUser: PlayerColor[],
                                              replayInitialPerspective: PlayerColor,
                                              userId: BigId | undefined, userMembership: ColonistMembershipType | undefined,
                                              gameTypeId: GameTypeEnum, accessLevel: AccessLevel | undefined) {
    if(canAccessReplayDataThroughSpecialPermissions(gameTypeId, accessLevel)) return allPlayerColors

    if(userId === undefined) return [replayInitialPerspective]

    if(userMembership === undefined) return ArrayUtils.getUniqueElements([replayInitialPerspective, ...playersColorsControlledByUser])

    return allPlayerColors
}

export function canAccessReplayDataThroughSpecialPermissions(gameTypeId: GameTypeEnum, accessLevel: AccessLevel | undefined): boolean {
    if(gameTypeId == GameTypeEnum.CreatedTournamentRoom) return true

    const validAccessLevels: AccessLevel[] = [AccessLevel.Administrator, AccessLevel.Manager, AccessLevel.Moderator]
    if(accessLevel != undefined && validAccessLevels.includes(accessLevel)) return true

    return false
}
